"use client";

import { MenuContext } from "@/context/menuContext";
import styles from "@/styles/modules/components/header/mainMenu.module.scss";
import { MenuItem } from "@/types/wordpress-parts";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { MouseEvent, useContext, useState } from "react";

interface MainMenuProps {
  mainMenu: MenuItem[];
}

export default function MainMenu({ mainMenu }: MainMenuProps) {
  const pathname = usePathname(); // ruta completa, ej: /dashboard/events
  const { isMenuOpen, closeMenu } = useContext(MenuContext);
  const [activeItems, setActiveItems] = useState<Set<string>>(new Set());

  const subMenuTogglerClick = (
    e: MouseEvent<HTMLDivElement>,
    itemId: string,
  ) => {
    e.stopPropagation();

    setActiveItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  // Filtrar solo los items de primer nivel
  const rootMenuItems = mainMenu.filter((item) => item.parentId === null);

  // Función recursiva para renderizar submenús
  function renderMenuItems(items: MenuItem[]) {
    return (
      <ul>
        {items.map((item) => {
          // Verificamos si hay hijos de forma segura
          const hasChildren = (item.childItems?.nodes?.length ?? 0) > 0;

          return (
            <li
              key={item.id}
              className={`${activeItems.has(item.id) ? styles.active : ""}`}
            >
              <div>
                <Link
                  href={item.fullUrl}
                  target={item.target || undefined}
                  className={`${
                    pathname === item.path ||
                    pathname.startsWith(item.path + "/")
                      ? styles.current
                      : ""
                  }`}
                >
                  {item.label}
                </Link>

                {hasChildren && (
                  <div onClick={(e) => subMenuTogglerClick(e, item.id)}>
                    <svg
                      width="23"
                      height="23"
                      viewBox="0 0 23 23"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <line
                        x1="20.7634"
                        y1="12.0138"
                        x2="2.51744"
                        y2="12.0138"
                        stroke="#1D1D1B"
                        strokeWidth="2"
                      />
                      <line
                        x1="12.0111"
                        y1="2.52013"
                        x2="12.0111"
                        y2="20.7661"
                        stroke="#1D1D1B"
                        strokeWidth="2"
                      />
                    </svg>
                  </div>
                )}
              </div>

              {hasChildren && renderMenuItems(item.childItems!.nodes)}
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <section
      className={`${styles.mainMenu} ${isMenuOpen ? styles.opened : ""}`}
    >
      <div className={styles.closer}>
        <Image
          src="/icons/close-icon.svg"
          alt="Close menu icon"
          width="41"
          height="31"
          onClick={closeMenu}
        />
      </div>
      <div className={styles.menu}>
        <nav>{renderMenuItems(rootMenuItems)}</nav>
      </div>
    </section>
  );
}
